import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser, SignInButton } from "../hooks/useAuthCompat";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import {
  MessageSquare, Radio, Film, FileText, Paperclip, CreditCard, Lock,
  Loader2, X, Play,
} from "lucide-react";

const SECTIONS = [
  { key: "signal", label: "Signal", icon: MessageSquare, type: "signal", tier: "gold" },
  { key: "episode", label: "Episodes", icon: Radio, type: "episode", tier: "gold" },
  { key: "video", label: "Videos", icon: Film, type: "video", tier: "gold" },
  { key: "framework", label: "Frameworks", icon: FileText, type: "framework", tier: "gold_pro" },
  { key: "companion_asset", label: "Companion", icon: Paperclip, type: "companion_asset", tier: "gold_pro" },
  { key: "billing", label: "Billing", icon: CreditCard, type: null, tier: "gold" },
];

const EMPTY_COPY = {
  signal: "No signal posted yet.",
  episode: "No episodes available yet.",
  video: "Gold video briefings will appear here.",
  framework: "No documents available yet.",
  companion_asset: "No Companion Assets have been published yet.",
};

function LockedUpsell() {
  return (
    <div className="rounded-2xl border border-[#C9A227]/20 p-8 text-center max-w-lg mx-auto"
      style={{ background: "radial-gradient(circle at 50% 0%, rgba(201,162,39,0.04) 0%, transparent 60%), #0a0808" }}
      data-testid="vault-locked-upsell">
      <div className="w-10 h-10 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
        <Lock className="w-4 h-4 text-[#C9A227]" />
      </div>
      <p className="text-[#C9A227] font-meta text-[10px] uppercase tracking-[0.2em] mb-3">Gold Pro</p>
      <h3 className="font-display text-xl mb-3">This section is part of Gold Pro</h3>
      <p className="text-white/50 text-sm mb-6">Efficiency Frameworks and Companion Assets are available on Gold Pro — everything in GOLD, plus the tools.</p>
      <Link to="/gold#memberships" data-testid="vault-upgrade-link"
        className="inline-block h-11 px-6 rounded-lg text-sm font-semibold" style={{ background: "#C9A227", color: "#0a0a0a", lineHeight: "44px" }}>
        Upgrade to Gold Pro — $100/mo
      </Link>
    </div>
  );
}

function ContentModal({ item, onClose }) {
  const api = useApi();
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/gold/content/${item.id}`);
        if (cancelled) return;
        setFull(res.data);
      } catch {
        if (cancelled) return;
        toast.error("Unable to open this resource.");
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  // Direct, cookie-authenticated media URL — the <audio>/<video> element
  // issues real HTTP Range requests against this itself (streams/seeks
  // without downloading the whole file first), instead of pre-fetching a blob.
  const mediaUrl = full?.file_url ? `${process.env.REACT_APP_BACKEND_URL}${full.file_url}` : null;
  const isVideoFile = mediaUrl && item.type === "video";

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-6" onClick={onClose} data-testid="vault-content-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative bg-[#111] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <button data-testid="vault-content-modal-close" onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-white/40 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        {loading ? (
          <div className="flex items-center gap-2 text-white/50 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : full && (
          <>
            <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9A227] mb-2">
              {full.tier.replace("_", " ")} · {full.type.replace("_", " ")}{full.category ? ` · ${full.category}` : ""}
            </p>
            <h3 className="font-display text-xl font-semibold mb-4 pr-8 break-words">
              {full.episode_number != null ? `Ep. ${full.episode_number} — ` : ""}{full.title}
            </h3>
            {full.publication_date && <p className="text-white/40 text-xs mb-4">Published {full.publication_date}</p>}
            {full.description && <p className="text-white/60 text-sm mb-4 whitespace-pre-line">{full.description}</p>}
            {full.body && <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line mb-4">{full.body}</p>}
            {full.external_video_url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                <iframe src={full.external_video_url} title={full.title} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
              </div>
            )}
            {isVideoFile && (
              <video controls src={mediaUrl} className="w-full rounded-lg mb-4 max-h-[50vh]" data-testid="vault-video-player" />
            )}
            {mediaUrl && item.type === "episode" && (
              <div className="bg-[#111] rounded-xl border border-white/10 p-4 mb-4">
                <audio controls src={mediaUrl} className="w-full" data-testid="vault-audio-player" />
              </div>
            )}
            {mediaUrl && (item.type === "framework" || item.type === "companion_asset") && (
              <a href={mediaUrl} target="_blank" rel="noreferrer" data-testid="vault-document-link"
                className="inline-block h-8 px-3 text-xs font-medium text-[#C9A227] border border-[#C9A227]/20 rounded-md hover:bg-[#C9A227]/5">
                Open Document
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ContentCard({ item, onOpen }) {
  const Icon = item.type === "video" ? Play : item.type === "episode" ? Radio : FileText;
  const thumbUrl = item.has_thumbnail ? `${process.env.REACT_APP_BACKEND_URL}/api/gold/content/${item.id}/thumbnail` : null;
  return (
    <button onClick={() => onOpen(item)} disabled={item.locked} data-testid={`vault-content-card-${item.id}`}
      className="text-left bg-[#0a0a0a] border border-white/[0.07] rounded-xl overflow-hidden hover:border-white/[0.12] transition-colors disabled:cursor-not-allowed disabled:opacity-60 w-full">
      {thumbUrl && <img src={thumbUrl} alt="" className="w-full h-28 object-cover" data-testid={`vault-content-thumbnail-${item.id}`} />}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <Icon className="w-4 h-4 text-[#C9A227]" />
          {item.locked && <Lock className="w-3.5 h-3.5 text-white/30" />}
        </div>
        <p className="text-white/90 font-medium text-sm leading-snug line-clamp-2 mb-1.5">{item.episode_number != null ? `Ep. ${item.episode_number} — ` : ""}{item.title}</p>
        {item.description && <p className="text-white/40 text-xs leading-relaxed line-clamp-2">{item.description}</p>}
      </div>
    </button>
  );
}

export default function Vault() {
  const api = useApi();
  const { user, isSignedIn, isLoaded } = useUser();
  const [section, setSection] = useState("signal");
  const [openItem, setOpenItem] = useState(null);

  const { data: membership, isLoading: membershipLoading } = useQuery({ queryKey: ["gold-membership"], queryFn: async () => (await api.get("/gold/membership")).data, enabled: !!isSignedIn });
  const activeTier = membership?.product_type;
  const current = SECTIONS.find((s) => s.key === section);
  const sectionLocked = current?.tier === "gold_pro" && activeTier !== "gold_pro";

  const { data: items, isLoading } = useQuery({
    queryKey: ["vault-content", current?.type],
    queryFn: async () => (await api.get("/gold/content", { params: { type: current.type } })).data,
    enabled: !!current?.type && !sectionLocked && !!isSignedIn,
  });

  const openBillingPortal = async () => {
    try {
      const res = await api.post("/billing/portal", null, { params: { origin_url: window.location.origin } });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal.");
    }
  };

  const onOpen = (item) => {
    if (item.locked) return toast.error("Members only — upgrade to unlock.");
    setOpenItem(item);
  };

  if (!isLoaded) return <div className="theme-gold min-h-screen" />;

  if (!isSignedIn) {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center text-center px-6" data-testid="vault-signin-required">
        <div>
          <p className="text-white/60 mb-4">Sign in to enter the Gold Vault.</p>
          <SignInButton>
            <button className="gold-metallic-btn px-6 py-3 text-sm uppercase tracking-wide rounded-lg">Sign In</button>
          </SignInButton>
        </div>
      </div>
    );
  }

  if (membershipLoading) {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center" data-testid="vault-membership-loading">
        <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
      </div>
    );
  }

  if (!activeTier) {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center text-center px-6" data-testid="vault-no-membership">
        <div>
          <p className="text-white/60 mb-4">You don't have an active Gold or Gold Pro membership yet.</p>
          <Link to="/gold" data-testid="vault-join-link" className="gold-metallic-btn inline-block px-6 py-3 text-sm uppercase tracking-wide rounded-lg">Join Gold</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-gold min-h-screen" data-testid="vault-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        {/* Status bar */}
        <div className="bg-[#0a0a0a] border border-white/[0.07] rounded-xl px-4 py-3 flex justify-between items-center flex-wrap gap-2" data-testid="vault-status-bar">
          <span className="text-white/60 text-sm truncate">{user?.primaryEmailAddress?.emailAddress}</span>
          <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-wide text-[#C9A227]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227] animate-pulse" />
            {activeTier === "gold_pro" ? "Gold Pro Active" : activeTier === "gold" ? "Gold Active" : "No Active Membership"}
          </span>
        </div>

        {/* Pills */}
        <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2" data-testid="vault-pills">
          {SECTIONS.map((s) => {
            const locked = s.tier === "gold_pro" && activeTier !== "gold_pro";
            const active = section === s.key;
            return (
              <button key={s.key} onClick={() => setSection(s.key)} data-testid={`vault-pill-${s.key}`}
                className="py-2.5 px-3 rounded-xl text-xs font-mono flex items-center justify-center gap-1.5 active:scale-95 transition-colors relative"
                style={active
                  ? { background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.5)", color: "#C9A227" }
                  : { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.5)" }}>
                <s.icon className="w-[11px] h-[11px]" /> {s.label}
                {locked && (
                  <span data-testid={`vault-pill-locked-badge-${s.key}`}
                    className="flex items-center justify-center w-4 h-4 rounded-full absolute -top-1.5 -right-1.5"
                    style={{ background: "#C9A227" }}>
                    <Lock className="w-2.5 h-2.5" style={{ color: "#0a0a0a" }} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Section content */}
        {section === "billing" ? (
          <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-7" data-testid="vault-billing-section">
            <p className="font-display text-lg mb-1">{activeTier === "gold_pro" ? "GOLD PRO" : "GOLD"} Subscription — {activeTier ? "Active" : "Inactive"}</p>
            <p className="text-white/50 text-sm mb-6">Manage your payment method, view invoices, or cancel anytime — cancelling keeps your access through the end of the current period.</p>
            <button data-testid="vault-manage-subscription-button" onClick={openBillingPortal}
              className="h-10 px-5 bg-transparent border border-white/15 text-white/80 hover:border-white/30 hover:bg-white/[0.04] rounded-lg text-sm transition-colors">
              Manage Subscription
            </button>
          </div>
        ) : sectionLocked ? (
          <LockedUpsell />
        ) : (
          <div>
            {isLoading && <div className="flex items-center gap-2 text-white/50 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
            {!isLoading && (!items || items.length === 0) && (
              <p className="text-white/30 text-sm py-10 text-center" data-testid="vault-empty-state">{EMPTY_COPY[current.type]}</p>
            )}
            {!isLoading && items?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`vault-content-grid-${current.type}`}>
                {items.map((item) => <ContentCard key={item.id} item={item} onOpen={onOpen} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {openItem && <ContentModal item={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
}
